import React, { useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Switch,
  FormControlLabel,
  Chip,
  Box,
} from "@mui/material";
import { db, storage } from "../firebase-config";
import { collection, addDoc, doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";

const ToolAdd = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState([]);
  const [currentTag, setCurrentTag] = useState("");
  const [file, setFile] = useState(null);
  const [isPublic, setIsPublic] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  
  const navigate = useNavigate();
  const auth = getAuth();
  const currentUser = auth.currentUser;

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleAddTag = () => {
    if (currentTag.trim() && !tags.includes(currentTag.trim())) {
      setTags((prevTags) => [...prevTags, currentTag.trim()]);
      setCurrentTag("");
    }
  };

  const handleDeleteTag = (tagToDelete) => {
    setTags((prevTags) => prevTags.filter((tag) => tag !== tagToDelete));
  };

  const handleSubmit = async () => {
    if (!title || !file) {
      setMessage("Please provide a title and select an HTML file.");
      return;
    }

    if (!currentUser) {
      setMessage("You must be logged in to add a tool.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      // Step 1: Add document to Firestore
      const toolRef = await addDoc(collection(db, "tools"), {
        title,
        description,
        ownerId: currentUser.uid,
        tags,
        isPublic,
        createdAt: new Date(),
      });

      // Step 2: Upload file to Storage
      const fileUrl = `tools/${toolRef.id}`;
      const storageRef = ref(storage, fileUrl);
      await uploadBytes(storageRef, file);

      // Step 3: Update Firestore document with the file path
      await updateDoc(doc(db, "tools", toolRef.id), {
        htmlPath: fileUrl,
      });

      setMessage("Tool successfully added!");
      
      // Reset form
      setTitle("");
      setDescription("");
      setTags([]);
      setCurrentTag("");
      setFile(null);
      setIsPublic(true);

      // Redirect to the view page
      navigate(`/tool/${toolRef.id}`);

    } catch (error) {
      setMessage("Error adding tool: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Typography variant="h4" gutterBottom>
        Add a New Tool
      </Typography>

      <TextField
        label="Tool Title"
        fullWidth
        margin="normal"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <TextField
        label="Description"
        fullWidth
        margin="normal"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <Box mt={2}>
        <Typography variant="body1" gutterBottom>
          Tags
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            label="Add Tag"
            value={currentTag}
            onChange={(e) => setCurrentTag(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleAddTag()}
          />
          <Button variant="contained" onClick={handleAddTag}>
            Add Tag
          </Button>
        </Box>
        <Box mt={1} display="flex" gap={1} flexWrap="wrap">
          {tags.map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              onDelete={() => handleDeleteTag(tag)}
              color="primary"
            />
          ))}
        </Box>
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
            color="primary"
          />
        }
        label="Public"
      />

      <input type="file" accept=".html" onChange={handleFileChange} style={{ marginTop: "20px" }} />

      {loading ? (
        <CircularProgress style={{ marginTop: "20px" }} />
      ) : (
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          style={{ marginTop: "20px" }}
        >
          Upload Tool
        </Button>
      )}

      {message && (
        <Typography variant="body1" color="secondary" style={{ marginTop: "20px" }}>
          {message}
        </Typography>
      )}
    </Container>
  );
};

export default ToolAdd;
